"use client";

import { useState } from "react";

import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";
import { BlogSearchIcon } from "@/assets/svg-icons/svg-icons";

interface HeroSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  description?: string;
  className?: string;
  totalResults?: number;
  initialFocus?: boolean;
}

export default function HeroSearch({
  searchQuery,
  onSearchChange,
  placeholder = "",
  description = "",
  className,
  totalResults,
  initialFocus = false,
}: HeroSearchProps) {
  const [isFocused, setIsFocused] = useState(initialFocus);

  return (
    <div className={cn("w-full max-w-[932px] mx-auto px-4", className)}>
      <div className="relative">
        {/* Non-focused state - Heading with underline */}
        {!isFocused && !searchQuery && (
          <div
            onClick={() => setIsFocused(true)}
            className="relative h-[70px] w-full cursor-text xl:h-[90px]"
          >
            <div className="absolute inset-0 flex items-center justify-between px-6">
              <TextComponent type="title" className="text-white/70">
                {placeholder}
              </TextComponent>
              <div className="xl:ml-4">
                <BlogSearchIcon className="size-[18px] text-white md:size-6 xl:size-10" />
              </div>
            </div>
            {/* Underline */}
            <div className="absolute bottom-0 h-[2px] w-full bg-white/40" />
          </div>
        )}
        {searchQuery && totalResults !== undefined && (
          <TextComponent
            type="body"
            className="my-2 text-right font-normal text-white xl:mb-[13px]"
          >
            {totalResults} {totalResults === 1 ? "result" : "results"} found
          </TextComponent>
        )}
        {/* Focused state - Input field */}
        {(isFocused || searchQuery) && (
          <div className="relative">
            <input
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                if (!searchQuery) setIsFocused(false);
              }}
              autoFocus={isFocused}
              className="h-[70px] w-full rounded-2xl border-2 border-white/20 bg-white/10 px-6 text-[35px] font-semibold text-white backdrop-blur-md transition-all duration-200 placeholder:text-white/50 focus:border-none focus:bg-[#05004E33] focus:outline-none xl:h-[90px] xl:rounded-[20px] xl:text-[50px]"
            />
            <div className="absolute right-6 top-1/2 -translate-y-1/2">
              <BlogSearchIcon className="size-10 text-white xl:size-12" />
            </div>
          </div>
        )}
      </div>

      {/* Description */}

      {description && (
        <TextComponent
          type="h4"
          className="mt-6 text-center font-normal text-white/90"
        >
          {description}
        </TextComponent>
      )}
    </div>
  );
}
